import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Printer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AppHeader } from "@/components/AppHeader";
import { api, errMsg } from "@/lib/api";
import { fmt, ddmmyyyy } from "@/lib/format";
import { monthLabel, monthRange } from "@/pages/SummaryPage";

export const personUrl = (p) => `/cashouts/person?name=${encodeURIComponent(p.unassigned ? "" : p.name)}`;

const PeopleTotals = ({ people, total, nav }) => (
  <section className="bg-white border border-slate-300" data-testid="cashout-people">
    <div className="flex items-center justify-between px-4 py-3 border-b-2 border-slate-900">
      <h2 className="font-extrabold text-base md:text-lg tracking-tight">Totals per person</h2>
      <span className="font-mono font-bold tabular-nums" data-testid="cashout-grand-total">{fmt(total)}</span>
    </div>
    <ul className="divide-y divide-slate-200">
      {people.map((p) => {
        return (
          <li key={p.name}>
            <button data-testid={`cashout-person-${p.name}`} onClick={() => nav(personUrl(p))} title="Open this person's ledger"
              className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-orange-50">
              <span className={`flex-1 min-w-0 truncate font-semibold ${p.unassigned ? "italic text-slate-500" : ""}`}>{p.name}</span>
              <span className="text-xs text-slate-500">{p.count}×</span>
              <span className="font-mono tabular-nums font-bold w-32 text-right" data-testid="cashout-person-total">{fmt(p.total)}</span>
              <ChevronRight size={14} className="text-slate-400" />
            </button>
          </li>
        );
      })}
    </ul>
  </section>
);

const CashoutList = ({ items, nav, month }) => (
  <section className="bg-white border border-slate-300" data-testid="cashout-list">
    <div className="ledger-scroll overflow-x-auto">
      <table className="w-full min-w-[560px] text-sm">
        <thead className="text-[10px] uppercase tracking-[0.12em] text-slate-500 bg-slate-50">
          <tr>{["Date", "Book", "Paid to", "Cashout", "Description", "Entered by"].map((h) => <th key={h} className={`px-3 py-2 font-bold ${h === "Cashout" ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.entry_id} data-testid="cashout-row" className="border-t border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => nav(`/book/${it.book_id}?${it.sheet === "sett" ? "sheet=sett&" : ""}${monthRange(month)}`)}>
              <td className="px-3 py-2.5 font-mono whitespace-nowrap">{ddmmyyyy(it.date)}</td>
              <td className="px-3 py-2.5 whitespace-nowrap">
                <span className="font-bold uppercase">{it.book_name}</span>
                {it.sheet === "sett" && <span className="ml-1.5 text-[10px] font-bold bg-amber-200 px-1.5">NEW SETT</span>}
              </td>
              <td className="px-3 py-2.5 font-semibold">{it.cashout_to || <span className="italic text-slate-400 font-normal">Not specified</span>}</td>
              <td className={`px-3 py-2.5 text-right font-mono tabular-nums font-semibold ${it.cashout < 0 ? "text-green-700" : "text-orange-800"}`}>{fmt(it.cashout)}</td>
              <td className="px-3 py-2.5 text-slate-700">{it.cashout_note}</td>
              <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">{it.by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

export default function CashoutsPage() {
  const nav = useNavigate();
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const { can } = useAuth();

  useEffect(() => {
    setData(null);
    api.get("/reports/cashouts", { params: { month: month || undefined } })
      .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month]);

  const idx = data ? data.months.indexOf(data.month) : -1;
  const go = (m) => setMonth(m);

  return (
    <div className="min-h-screen grid-paper">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">Who received the cash</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight">Cashout report</h1>
          </div>
          {data?.month && can("export") && (
            <Link to={`/cashouts/print?month=${data.month}`} data-testid="cashout-print-link"
              className="h-10 px-4 flex items-center gap-2 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 transition-colors order-last sm:order-none">
              <Printer size={15} /> Print / share
            </Link>
          )}
          {data?.month && (
            <div className="flex items-center border border-slate-300 bg-white" data-testid="cashout-month-nav">
              <button data-testid="cashout-prev-month" disabled={idx >= data.months.length - 1} onClick={() => go(data.months[idx + 1])}
                className="h-10 w-10 grid place-items-center hover:bg-slate-100 disabled:opacity-30"><ChevronLeft size={16} /></button>
              <select data-testid="cashout-month-select" value={data.month} onChange={(e) => go(e.target.value)}
                className="h-10 px-2 bg-transparent font-bold outline-none">
                {data.months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
              <button data-testid="cashout-next-month" disabled={idx <= 0} onClick={() => go(data.months[idx - 1])}
                className="h-10 w-10 grid place-items-center hover:bg-slate-100 disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          )}
        </div>
        {!data ? <p className="text-sm text-slate-500" data-testid="cashout-loading">Loading…</p>
          : !data.month ? (
            <div className="border border-dashed border-slate-400 bg-white/60 p-10 text-center" data-testid="cashout-empty">
              <p className="font-semibold">No cashouts recorded yet</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-[340px_1fr] gap-4 items-start fade-up">
              <PeopleTotals people={data.people} total={data.total} nav={nav} />
              <div className="min-w-0">
                <CashoutList items={data.items} nav={nav} month={data.month} />
              </div>
            </div>
          )}
      </main>
    </div>
  );
}
